import { forwardRef } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

import { TimesCircleIcon } from '../../icons/timesCircle'
import { sizing, focusedStyles, borderOutlineStyles, placeholderStyles } from '../styles'
import { base, inputReset } from '../../mixins'
import { PB70T, PB40T } from '../../colors'

const getPadding = ({ size, Icon, isClearable }) => {
  if (Icon && isClearable) {
    return sizing[size].iconClearable.padding
  }

  if (Icon) {
    return sizing[size].icon.padding
  }

  if (isClearable) {
    return sizing[size].clearable.padding
  }

  return sizing[size].padding
}
const Wrapper = styled.span`
  position: relative;
  width: 100%;
  display: inline-block;
`
const IconWrap = styled.span`
  position: absolute;
  top: ${({ size }) => sizing[size].icon.top};
  left: ${({ size }) => sizing[size].icon.left};
`
const ClearIconWrap = styled.span`
  position: absolute;
  top: ${({ size }) => sizing[size].icon.top};
  right: ${({ size }) => sizing[size].icon.right};

  > span[role='img'] {
    cursor: pointer;
  }
`
const Input = styled.input`
  ${inputReset};
  ${base};
  ${borderOutlineStyles};
  color: ${({ color }) => color};
  font-size: ${({ size }) => sizing[size].fontSize};
  padding: ${getPadding};
  width: 100%;
  line-height: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus {
    ${focusedStyles};
  }

  &::placeholder {
    ${placeholderStyles};
  }
`
const TextInput = forwardRef(function TextInput(
  {
    id,
    labelledBy,
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClear,
    icon: Icon,
    type = 'text',
    size = 'medium',
    color = PB40T,
    placeholder = '',
    borderColor = PB70T,
    isDisabled = false,
    ...rest
  },
  ref
) {
  const isClearable = typeof onClear === 'function'

  return (
    <Wrapper>
      <Input
        id={id}
        ref={ref}
        type={type}
        value={value}
        disabled={isDisabled}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        isClearable={isClearable}
        Icon={Icon}
        size={size}
        color={color}
        placeholder={placeholder}
        borderColor={borderColor}
        aria-labelledby={labelledBy}
        {...rest}
      />
      {Icon && (
        <IconWrap size={size}>
          <Icon />
        </IconWrap>
      )}
      {!isDisabled && isEmpty(value) && isClearable && (
        <ClearIconWrap size={size}>
          <TimesCircleIcon onClick={onClear} />
        </ClearIconWrap>
      )}
    </Wrapper>
  )
})

TextInput.propTypes = {
  /** The id of the input, useful for accessibility */
  id: PropTypes.string,
  /** The id of the associated label (i.e. `Field` component), useful for accessibility */
  labelledBy: PropTypes.string,
  /** The type of text input */
  type: PropTypes.oneOf(['text', 'password', 'hidden']),
  /** The value for the text input when controlled */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The callback when the user types in text input */
  onChange: PropTypes.func,
  /** The callback when the text input loses focus */
  onBlur: PropTypes.func,
  /** The callback when the text input receives focus */
  onFocus: PropTypes.func,
  /** The callback when a user clicks on the clear text icon */
  onClear: PropTypes.func,
  /** The callback when the text input receives keydown */
  onKeyDown: PropTypes.func,
  /** Whether the text input is disabled or not */
  isDisabled: PropTypes.bool,
  /** The icon to use in the input */
  icon: PropTypes.elementType,
  /** The color of the text inside the input */
  color: PropTypes.string,
  /** The placeholder for the input */
  placeholder: PropTypes.string,
  /** The border color of the input */
  borderColor: PropTypes.string,
  /** The relative size of the input */
  size: PropTypes.oneOf(['small', 'medium', 'large'])
}

export { TextInput }
